import { ApolloClient } from "apollo-client";
import { ApolloLink, split } from "apollo-link";
import { HttpLink } from "apollo-link-http";
import { WebSocketLink } from "apollo-link-ws";
import { getMainDefinition, getOperationName } from "apollo-utilities";
import { onError } from "apollo-link-error";
import { InMemoryCache } from "apollo-cache-inmemory";
import gql from "graphql-tag";
import { Config } from "constant";
import { UpdateCommonStoreConfig, CommonStore } from "./Store";

function createHTTPLink() {
  return new HttpLink({
    uri: Config.graphqlUrl,
    credentials: "include"
  });
}

function createAnalyticHTTPLink() {
  return new HttpLink({
    uri: Config.analyticGraphqlUrl,
    credentials: "include"
  });
}

function createWSLink() {
  return new WebSocketLink({
    uri: Config.socketUrl,
    options: {
      reconnect: true,
      credentials: "include",
      lazy: true,
      timeout: 15000,
      connectionParams: {
        timeout: 15000
      }
    }
  });
}

function createLogger() {
  return new ApolloLink((operation, forward) => {
    // uncomment this to log all fetched operationName
    return forward(operation);
  });
}

// you may set this off to avoid global redirect
let unauthorizedRedirect = true;
function createErrorHandler() {
  return onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors)
      graphQLErrors.map(({ message, locations, path }) =>
        console.log(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
        )
      );

    // global redirection if unauthorized
    if (unauthorizedRedirect && networkError.statusCode === 401) {
      window.location = "/login";
    }
  });
}

let client;
// function to get the only instance of full Apollo Client
export function GetClient() {
  if (!client) {
    
    const wsLink = createWSLink();
    const loggerLink = createLogger();
    const errorLink = createErrorHandler();
    const httpLink = createHTTPLink();
    const analyticHttpLink = createAnalyticHTTPLink();
    const normalHttpLink = split(
      ({ query }) => {
        return query.isAnalytic   
      },
      errorLink.concat(loggerLink.concat(analyticHttpLink)),
      errorLink.concat(loggerLink.concat(httpLink))  
    )
    // combine links
    const link = split(
      // split based on operation type
      ({ query }) => {
        const { kind, operation } = getMainDefinition(query);
        return kind === "OperationDefinition" && operation === "subscription";
      },
      errorLink.concat(loggerLink.concat(wsLink)),
      normalHttpLink
    );

    client = new ApolloClient({
      link: link,
      cache: new InMemoryCache()
    });
  }

  return client;
}

export async function CheckIsLoggedIn() {
  unauthorizedRedirect = false;
  let result;
  try {
    await GetClient().query({
      query: queryGetCompany
    });
    result = true;
  } catch (error) {
    if (error.networkError.statusCode === 401) {
      result = false;
    } else {
      result = true;
    }
  }
  unauthorizedRedirect = true;
  return result;
}

export async function LoadCompanyConfiguration() {
  try {
    const result = await GetClient().query({
      query: queryGetCompanyConfiguration
    })
    if(result.data.getCompanyConfiguration) {
      UpdateCommonStoreConfig(result.data.getCompanyConfiguration)
    }
    return CommonStore
  } catch(e) {
    //error on something
  }
}

export async function Login(company, username, password) {
  try {
    const result = await GetClient().mutate({
      mutation: mutationLogin,
      variables: { company, username, password }
    });
    return result && result.data && result.data.logIn;
  } catch (error) {
    return false;
  }
}

/* Graphql Fragments */

const GraphBaseResultTypeFragment = gql`
  fragment GraphBaseResultTypeFragment on GraphBaseResultType {
    status
    message
  }
`;

const CompanyFragment = gql`
  fragment CompanyFragment on Company {
    id
    displayName
    description
    theme
    logo
    quota
    package {
      id
      name
      price
      feature
    }
    expired
  }
`;
const CompanyConfigurationFragment = gql`
  fragment CompanyConfigurationFragment on CompanyConfiguration {
    id
    workingStart
    workingEnd
    responseRateSLA
    responseTimeSLA
    handlingTimeSLA
    totalActiveAgents
    totalQuota
    nickActive
    replyLabelActive
    iconCompanyActive
    autoAssignActive
    csatActive
    allowAgentAssignToOther
    enableTicketing
    enableEmailCampaign
    enableBulkSms
  }
`;
const OnlineStatusTypeFragment = gql`
  fragment OnlineStatusTypeFragment on OnlineStatusType {
    id
    name
    displayName
  }
`;
const CustomerFieldTypeFragment = gql`
  fragment CustomerFieldTypeFragment on CustomerFieldType {
    id
    displayName
    name
  }
`;



const CustomerFieldFragment = gql`
  fragment CustomerFieldFragment on CustomerField {
    id
    value
    fieldType {
      ...CustomerFieldTypeFragment
    }
  }
  ${CustomerFieldTypeFragment}
`;
const ExternalMediaFragment = gql`
  fragment ExternalMediaFragment on ExternalMedia {
    id
    url
    previewUrl
    type
  }
`;

const LabelTemplateFragment = gql`
  fragment LabelTemplateFragment on LabelTemplate {
    id
    parentId
    crCode
    name
    displayName
    order
    color
    labelType
    isActive
    isShown
    dateCreated
  }
`;

const NoteTypeFragment = gql`
  fragment NoteTypeFragment on NoteType {
    id
    content
    dateCreated
    noteTypeType
  }
`;

const ChannelSpecificTypeFragment = gql`
  fragment ChannelSpecificTypeFragment on ChannelSpecificType {
    id
    name
    charLimit
    isReplyMethod
  }
`;

const DistributionsConditionTypeFragment = gql`
  fragment DistributionsConditionTypeFragment on DistributionCondition {
    id
    content
    distributionsType
    dateCreated
    dateUpdated
  }
`;

const TemplateReplyFragment = gql`
  fragment TemplateReplyFragment on TemplateReplyType {
    id
    content
    dateCreated
  }
`;

const ChannelFragment = gql`
  fragment ChannelFragment on Channel {
    id
    name
    channelType
    channelSpecificType {
      ...ChannelSpecificTypeFragment
    }
  }
  ${ChannelSpecificTypeFragment}
`;

const AgentFragment = gql`
  fragment AgentFragment on Agent {
    id
    shortName
    username
    displayName
    displayPicture
    email
    role {
      id
      name
    }
    labels {
      id
      labelTemplate {
        ...LabelTemplateFragment
      }
    }
    onlineStatus {
      onlineStatusType {
        ...OnlineStatusTypeFragment
      }
    }
  }
  ${OnlineStatusTypeFragment}
  ${LabelTemplateFragment}
`;

const CsatTemplateFragment = gql`
  fragment CsatTemplateFragment on TemplateCsatType {
    id
    content
    createdBy {
      ...AgentFragment
    }
    dateCreated
  }
  ${AgentFragment}
`;

const CompanyChannelHandlerFragment = gql`
  fragment CompanyChannelHandlerFragment on CompanyChannelHandler {
    id
    originalId
    name
    displayName
    displayPicture
    dateCreated
    channel {
      ...ChannelFragment
    }
    nickName
    color
  }
  ${ChannelFragment}
`;

const CompanyContextStreamFragment = gql`
  fragment CompanyContextStreamFragment on CompanyContextStreamType {
    id
    content
    channelHandler {
      id
      name
    }
    media {
      ...ExternalMediaFragment
    }
    dateCreated
    secondaryOriginalId
  }
`;

const StreamFragment = gql`
  fragment StreamFragment on Stream {
    id
    originalId
    fromId
    caseId
    content
    replyMode
    replyToStream {
      id
      content
      createdBy {
        id
        displayName
      }
    }
    createdBy {
      id
      displayName
      displayPicture
    }
    dateCreated
    labels {
      labelTemplate {
        ...LabelTemplateFragment
      }
    }
    media {
      ...ExternalMediaFragment
    }
    specificTypeId {
      ...ChannelSpecificTypeFragment
    }
    companyContextStream {
      ...CompanyContextStreamFragment
    }
    channelHandler {
      ...CompanyChannelHandlerFragment
    }
    channel {
      ...ChannelFragment
    }
    isOldCase
  }

  ${LabelTemplateFragment}
  ${ExternalMediaFragment}
  ${ChannelSpecificTypeFragment}
  ${CompanyContextStreamFragment}
  ${CompanyChannelHandlerFragment}
  ${ChannelFragment}
`;

const CustomersChannelHandlerFragment = gql`
  fragment CustomersChannelHandlerFragment on CustomerChannelHandler {
    id
    originalId
    name
    displayName
    displayPicture
    dateCreated
    channel {
      ...ChannelFragment
    }
    lastStream {
      ...StreamFragment
    }
  }
  ${ChannelFragment}
  ${StreamFragment}
`;

const CustomerFragment = gql`
  fragment CustomerFragment on Customer {
    id
    name
    labels {
      labelTemplate {
        ...LabelTemplateFragment
      }
    }
    noteInfo {
      ...NoteTypeFragment
    }
    fields {
      ...CustomerFieldFragment
    }
    channelHandler {
      ...CustomersChannelHandlerFragment
    }
  }
  ${LabelTemplateFragment}
  ${NoteTypeFragment}
  ${CustomerFieldFragment}
  ${CustomersChannelHandlerFragment}
`;

const CustomerChannelHandlerFragment = gql`
  fragment CustomerChannelHandlerFragment on CustomerChannelHandler {
    id
    name
    displayName
    displayPicture
  }
`;

const StreamConnectionFragment = gql`
  fragment StreamConnectionFragment on StreamConnection {
    edges {
      cursor
      node {
        ...StreamFragment
      }
    }
  }

  ${StreamFragment}
`;

const PendingStreamFragment = gql`
  fragment PendingStreamFragment on PendingStream {
    id
    caseId
    content
    createdBy {
      id
      displayPicture
    }
    replyToStream {
      id
      content
    }
    dateCreated
    escalation {
      escalatedTo {
        id
      }
      status
    }
    isRevised
    noteClose {
      ...NoteTypeFragment
    }
  }
  ${NoteTypeFragment}
`;

const PendingStreamConnectionFragment = gql`
  fragment PendingStreamConnectionFragment on PendingStreamConnection {
    edges {
      cursor
      node {
        ...PendingStreamFragment
      }
    }
  }
  ${PendingStreamFragment}
`;

const CaseFragment = gql`
  fragment CaseFragment on Case {
    id
    dateUpdated
    progressType
    isEscalated
    assignedTo {
      ...AgentFragment
    }
    lastStream {
      ...StreamFragment
    }
    labels {
      labelTemplate {
        ...LabelTemplateFragment
      }
    }
    customerChannelHandler {
      ...CustomerChannelHandlerFragment
    }
    noteClose {
      ...NoteTypeFragment
    }
    noteInfo {
      ...NoteTypeFragment
    }
    channel {
      ...ChannelFragment
    }
    lastPendingStream {
      ...PendingStreamFragment
    }
    pendingStreams {
      ...PendingStreamFragment
    }
    channelHandler {
      ...CompanyChannelHandlerFragment
    }
  }

  ${AgentFragment}
  ${StreamFragment}
  ${PendingStreamFragment}
  ${LabelTemplateFragment}
  ${CustomerChannelHandlerFragment}
  ${ChannelFragment}
  ${NoteTypeFragment}
  ${CompanyChannelHandlerFragment}
`;

const CaseConnectionFragment = gql`
  fragment CaseConnectionFragment on CaseConnection {
    edges {
      cursor
      node {
        ...CaseFragment
      }
    }
  }
  ${CaseFragment}
`;

const SingleAggregateResultFragment = gql`
  fragment SingleAggregateResultFragment on SingleAggregateResult {
    name
    value
    details {
      name
      value
    }
  }
`;

const AggregateResultFragment = gql`
  fragment AggregateResultFragment on AggregateResult {
    description
    data {
      ...SingleAggregateResultFragment
    }
  }

  ${SingleAggregateResultFragment}
`;

const PerformActionTypeFragment = gql`
  fragment PerformActionTypeFragment on PerformActionType {
    id
    actionType
    actionTo
    createdBy {
      ...AgentFragment
    }
    labelTemplate {
      ...LabelTemplateFragment
    }
    dateCreated
    dateUpdated
  }
  ${AgentFragment}
  ${LabelTemplateFragment}
`;

const ActionTypeFragment = gql`
  fragment ActionTypeFragment on ActionType {
    id
    company {
      ...CompanyFragment
    }
    createdBy {
      ...AgentFragment
    }
    performActionType {
      ...PerformActionTypeFragment
    }
    dateCreated
  }
  ${CompanyFragment}
  ${AgentFragment}
  ${PerformActionTypeFragment}
`;

const DistributionsTypeFragment = gql`
  fragment DistributionsTypeFragment on DistributionsType {
    id
    name
    description
    action {
      ...ActionTypeFragment
    }
    distributionsCondition {
      ...DistributionsConditionTypeFragment
    }
    createdBy {
      ...AgentFragment
    }
    dateCreated
    dateUpdated
    isShown
  }
  ${ActionTypeFragment}
  ${DistributionsConditionTypeFragment}
  ${AgentFragment}
`;

const TemplateListTypeFragment = gql`
  fragment TemplateListTypeFragment on TemplateListType {
    id
    templateReply {
      ...TemplateReplyFragment
    }
    createdBy {
      ...AgentFragment
    }
    content
    dateCreated
  }
  ${TemplateReplyFragment}
  ${AgentFragment}
`;

const SchedulerPostFragment = gql`
  fragment SchedulerPostFragment on SchedulerPost {
    id
    channel {
      ...ChannelFragment
    }
    specificType {
      ...ChannelSpecificTypeFragment
    }
    content
    sentBy {
      ...AgentFragment
    }
    dateForPost
    isSentEmail
    dateCreated
    companyChannelHandler {
      ...CompanyChannelHandlerFragment
    }
    isActive
    media {
      ...ExternalMediaFragment
    }
  }

  ${ChannelFragment}
  ${ChannelSpecificTypeFragment}
  ${AgentFragment}
  ${CompanyChannelHandlerFragment}
  ${ExternalMediaFragment}
`;

const GroupSchedulerPostFragment = gql`
  fragment GroupSchedulerPostFragment on GroupSchedulerPost {
    id
    name
    schedulerPost {
      ...SchedulerPostFragment
    }
    dateCreated
    createdBy {
      ...AgentFragment
    }
  }
  ${SchedulerPostFragment}
  ${AgentFragment}
`;

const SchedulerPostConnectionFragment = gql`
  fragment SchedulerPostConnectionFragment on SchedulerPostConnection {
    edges {
      cursor
      node {
        ...SchedulerPostFragment
      }
    }
  }
  ${SchedulerPostFragment}
`;

const CustomerListFragment = gql`
  fragment CustomerListFragment on CustomerListConnection {
    edges {
      cursor
      node {
        ...CustomerFragment
      }
    }
  }
  ${CustomerFragment}
`;

const CustomerJourneyFragment = gql`
  fragment CustomerJourneyFragment on CustomerJourneyListConnection {
    edges {
      cursor
      node {
        id
        case {
          ...CaseFragment
        }
        actionFrom {
          id
          name
          displayPicture
          channelName
        }
        actionTo {
          id
          name
          displayPicture
          channelName
        }
        content
        actionTypeCustomerJourney
        operationTypeCustomerJourney
        dateBegin
        dateEnd
        dateCreated
      }
    }
  }
  ${CaseFragment}
`;

const NotificationGroupTypeFragment = gql`
  fragment NotificationGroupTypeFragment on NotificationGroupType {
    company {
      ...CompanyFragment
    }
    agent {
      ...AgentFragment
    }
    notificationGroup {
      id
      caseId
      isRead
      readTimestamp
      isActionable
      userReadableContent
      dateCreated
      content
      channelHandlerUsername
      assignedTo
    }
  }
  ${CompanyFragment}
  ${AgentFragment}
`;

const BillingTypeFragment = gql`
  fragment BillingTypeFragment on BillingType {
    id
    noInvoice
    paymentDate
    discount
    subtotal
    total
    status
    dateCreated
  }
`;

const PackageModelTypeFragment = gql`
  fragment PackageModelTypeFragment on ModelType {
    id
    name
    price
    feature
    dateCreated
  }
`;

const TemplateLivechatTypeFragment = gql`
  fragment TemplateLivechatTypeFragment on TemplateLivechatType {
    id
    company {
      ...CompanyFragment
    }
    createdBy {
      ...AgentFragment
    }
    content
    dateCreated
    isActive
  }
  ${CompanyFragment}
  ${AgentFragment}
`;
const TicketFragment = gql`
  fragment TicketFragment on TicketType {
    id
    ticketId
    ticketCode
    channel {
      ...ChannelFragment
    }
    customerChannelHandler {
      ...CustomerChannelHandlerFragment
    }
    companyChannelHandler {
      ...CompanyChannelHandlerFragment
    }
    title
    content
    phoneNumber
    email
    address
    dateCreated
    status
  }
  ${ChannelFragment}
  ${CustomerChannelHandlerFragment}
  ${CompanyChannelHandlerFragment}
`;
/* Graphql Queries */

export const queryGetBrandOfTicketing = gql`
  query getBrandOfTicketing {
    getBrandOfTicketing {
      id
      name
      displayName
    }
  }
`;

export const queryGetProductOfTicketing = gql`
  query getProductOfTicketing {
    getProductOfTicketing {
      id
      name
    }
  }
`;

export const queryGetTemplateLivechatCompany = gql`
  query getTemplateLivechatCompany {
    getTemplateLivechatCompany {
      ...TemplateLivechatTypeFragment
    }
  }
  ${TemplateLivechatTypeFragment}
`;

export const queryGetOngkirCity = gql`
  query getOngkirCity {
    getOngkirCity {
      city_id
      province_id
      province
      type
      city_name
      postal_code
    }
  }
`;

export const queryGetOngkirCompany = gql`
  query getOngkirCompany {
    getOngkirCompany {
      id
      template
      companyId
      isActive
    }
  }
`;

export const queryCekOngkir = gql`
  query cekOngkir($originId: Int!, $destinationId: Int!, $weight: Int!) {
    cekOngkir(
      originId: $originId
      destinationId: $destinationId
      weight: $weight
    ) {
      courier
      name
      logo
      service
      description
      cost
      etd
      note
      weight
      origin
      destination
    }
  }
`;

export const queryCheckDomainVerify = gql`
  query checkDomainVerify($companyChannelHandlerId: Int!) {
    checkDomainVerify(companyChannelHandlerId: $companyChannelHandlerId) {
      ...CompanyChannelHandlerFragment
    }
  }
  ${CompanyChannelHandlerFragment}
`;

export const queryGetServerTime = gql`
  query getServerTime {
    getServerTime
  }
`;

export const queryGetOnlineStatus = gql`
  query getOnlineStatus {
    getOnlineStatusType {
      ...OnlineStatusTypeFragment
    }
  }
  ${OnlineStatusTypeFragment}
`;

export const queryGetCompany = gql`
  query getCompany {
    getCompany {
      ...CompanyFragment
    }
  }
  ${CompanyFragment}
`;

export const queryGetCustomerFieldType = gql`
  query getCustomerFieldType {
    getCustomerFieldType {
      ...CustomerFieldTypeFragment
    }
  }
  ${CustomerFieldTypeFragment}
`;

export const queryGetCompanyConfiguration = gql`
  query getCompanyConfiguration {
    getCompanyConfiguration {
      ...CompanyConfigurationFragment
    }
  }
  ${CompanyConfigurationFragment}
`;

export const queryGetStream = gql`
  query getStream($case: CaseInput!) {
    getStreamsFromCase(case: $case) {
      ...StreamFragment
    }
  }
  ${StreamFragment}
`;

export const queryGetStreamsPagination = gql`
  query getStreamsPagination($case: CaseInput!, $after: String) {
    getStreamsPagination(case: $case, last: 10, after: $after)
      @connection(key: "streamsPagination", filter: ["case"]) {
      ...StreamConnectionFragment
    }
  }
  ${StreamConnectionFragment}
`;

export const queryGetPendingStreamByCasePagination = gql`
  query getPendingStreamByCasePagination($case: CaseInput!, $after: String) {
    getPendingStreamByCasePagination(case: $case, last: 6, after: $after)
      @connection(key: "pendingStreamsPagination", filter: ["case"]) {
      ...PendingStreamConnectionFragment
    }
  }
  ${PendingStreamConnectionFragment}
`;

export const queryGetCaseById = gql`
  query getCaseById($case: CaseInput!) {
    getCaseById(case: $case) {
      ...CaseFragment
    }
  }
  ${CaseFragment}
`;

export const queryGetCasesPagination = gql`
  query getCasesPagination(
    $isUnassigned: Boolean
    $searchContent: String
    $startTime: DateTime
    $endTime: DateTime
    $agent: AgentInput
    $searchChannel: channel
    $searchLabels: [SingleLabelInput!]
    $searchCustomer: String
    $after: String
  ) {
    getCasesPagination(
      isUnassigned: $isUnassigned
      searchContent: $searchContent
      startTime: $startTime
      endTime: $endTime
      agent: $agent
      searchChannel: $searchChannel
      searchLabels: $searchLabels
      customerChannelHandler: $searchCustomer
      first: 10
      after: $after
    ) {
      ...CaseConnectionFragment
    }
  }
  ${CaseConnectionFragment}
`;

export const queryGetCasesPendingApprovalPagination = gql`
  query getCasesPendingApprovalPagination($after: String) {
    getCasesPendingApprovalPagination(after: $after, first: 10) {
      ...CaseConnectionFragment
    }
  }
  ${CaseConnectionFragment}
`;

export const queryGetCasesClosedPagination = gql`
  query getCasesClosedPagination(
    $searchContent: String
    $startTime: DateTime
    $endTime: DateTime
    $agent: AgentInput
    $searchChannel: channel
    $searchLabels: [SingleLabelInput!]
    $searchCustomer: String
    $after: String
  ) {
    getCasesClosedPagination(
      progressTypeInput: "close"
      searchContent: $searchContent
      startTime: $startTime
      endTime: $endTime
      agent: $agent
      searchChannel: $searchChannel
      searchLabels: $searchLabels
      customerChannelHandler: $searchCustomer
      first: 10
      after: $after
    ) {
      ...CaseConnectionFragment
    }
  }
  ${CaseConnectionFragment}
`;

export const queryGetAgents = gql`
  query getAgents {
    getAgents {
      ...AgentFragment
    }
  }

  ${AgentFragment}
`;

export const queryGetMe = gql`
  query getMe {
    me {
      agent {
        ...AgentFragment
      }
      company {
        ...CompanyFragment
      }
    }
  }

  ${AgentFragment}
  ${CompanyFragment}
`;

export const queryGetChannels = gql`
  query getChannels {
    getCompanyChannelHandler {
      ...CompanyChannelHandlerFragment
    }
  }

  ${CompanyChannelHandlerFragment}
`;

export const queryGetLabels = gql`
  query getLabels {
    caseLabels: getLabelTemplates(labelType: CASE) {
      ...LabelTemplateFragment
    }
    customerLabels: getLabelTemplates(labelType: CUSTOMER) {
      ...LabelTemplateFragment
    }
    streamLabels: getLabelTemplates(labelType: STREAM) {
      ...LabelTemplateFragment
    }
  }
  ${LabelTemplateFragment}
`;

export const queryGetLabelsByType = gql`
  query getLabels($type: LabelType) {
    getLabelTemplates(labelType: $type) {
      ...LabelTemplateFragment
    }
  }
  ${LabelTemplateFragment}
`;

export const queryGetCustomerFromCase = gql`
  query getCustomerFromCase($case: CaseInput!) {
    getCustomerFromCase(case: $case) {
      ...CustomerFragment
    }
  }
  ${CustomerFragment}
`;

export const queryGetTicketFromCase = gql`
  query getTicketFromCase($case: CaseInput!) {
    getTicketByCase(case: $case) {
      ...TicketFragment
    }
  }
  ${TicketFragment}
`;

export const queryGetCaseAggregateCount = gql`
  query getCaseAggregateCount {
    getCaseAggregateCount {
      assignedToCount
      lastReplyCount
      allCount
      pendingApprovalCount
    }
  }
`;

export const queryGetCaseCloseAggregateCount = gql`
  query getCaseCloseAggregateCount {
    getCaseCloseAggregateCount
  }
`;

export const queryGetAutomationDistributions = gql`
  query getAutomationDistributions {
    getAutomationDistributions {
      ...DistributionsTypeFragment
    }
  }
  ${DistributionsTypeFragment}
`;

export const queryGetTemplateList = gql`
  query getTemplateList {
    getTemplateList {
      ...TemplateListTypeFragment
    }
  }
  ${TemplateListTypeFragment}
`;

export const queryGetTemplateCsat = gql`
  query getTemplateCsat {
    getTemplateCsat {
      ...CsatTemplateFragment
    }
  }
  ${CsatTemplateFragment}
`;

/*Graphql Query -- Scheduler */

export const queryGetSchedulerPost = gql`
  query getSchedulerPost(
    $after: String
    $startTime: DateTime
    $endTime: DateTime
    $searchContent: String
    $agent: AgentInput
    $searchChannel: channel
  ) {
    getSchedulerPost(
      after: $after
      startTime: $startTime
      endTime: $endTime
      searchContent: $searchContent
      agent: $agent
      searchChannel: $searchChannel
      first: 10
    ) {
      ...SchedulerPostConnectionFragment
    }
  }
  ${SchedulerPostConnectionFragment}
`;

export const queryGetSchedulerPostSent = gql`
  query getSchedulerPostSent(
    $after: String
    $startTime: DateTime
    $endTime: DateTime
    $searchContent: String
    $agent: AgentInput
    $searchChannel: channel
  ) {
    getSchedulerPostSent(
      after: $after
      startTime: $startTime
      endTime: $endTime
      searchContent: $searchContent
      agent: $agent
      searchChannel: $searchChannel
      first: 10
    ) {
      ...SchedulerPostConnectionFragment
    }
  }
  ${SchedulerPostConnectionFragment}
`;

/*Graphql Query -- Customer List */

export const queryGetCustomerListPagination = gql`
  query getCustomerListPagination(
    $startTime: DateTime
    $endTime: DateTime
    $searchChannel: channel
    $searchLabels: [SingleLabelInput!]
    $searchCustomer: String
    $after: String
  ) {
    getCustomerListPagination(
      startTime: $startTime
      endTime: $endTime
      searchChannel: $searchChannel
      searchLabels: $searchLabels
      customerChannelHandler: $searchCustomer
      first: 20,
      after: $after
    ) {
      ...CustomerListFragment
    }
  }
  ${CustomerListFragment}
`;

export const queryGetCustomerJourney = gql`
  query getCustomerJourney($after: String, $id: Int!) {
    getCustomerJourney(
      first: 10
      after: $after
      customerChannelHandlerId: $id
    ) {
      ...CustomerJourneyFragment
    }
  }
  ${CustomerJourneyFragment}
`;

export const queryGetCustomerDetail = gql`
  query getCustomerDetail($id: Int!) {
    getCustomerDetail(customerChannelHandlerId: $id) {
      ...CustomerFragment
    }
  }
  ${CustomerFragment}
`;

export const queryGetNotification = gql`
  query getNotification {
    getNotification {
      ...NotificationGroupTypeFragment
    }
  }
  ${NotificationGroupTypeFragment}
`;

export const queryGetAllPackage = gql`
  query getAllPackage {
    getAllPackage {
      ...PackageModelTypeFragment
    }
  }
  ${PackageModelTypeFragment}
`;

export const queryGetBillingByCompanyId = gql`
  query getBillingByCompanyId {
    getBillingByCompanyId {
      ...BillingTypeFragment
    }
  }
  ${BillingTypeFragment}
`;

export const queryGetBillingHistory = gql`
  query getBillingHistory($billingId: Int!) {
    getBillingHistory(billingId: $billingId) {
      id
      billing {
        ...BillingTypeFragment
      }
      package {
        ...PackageModelTypeFragment
      }
      agentQuota
      topup
      expiredDate
      price
      dateCreated
    }
  }
  ${BillingTypeFragment}
  ${PackageModelTypeFragment}
`;

/*Graphql Query -- Analytic */

const createAnalyticQuery = name => {
  const definition =  gql`
    query get${name}(
      $granularity: String!, 
      $startTime: DateTime!,
      $endTime: DateTime!,
      $channelList: [channel!]!,
      $companyChannelHandlerList: [CompanyChannelHandlerInput!]!,
      $agentList: [AgentInput!]!  
      ) {
      get${name}(
        granularity: $granularity, 
        startTime: $startTime,
        endTime: $endTime,
        channelList: $channelList,
        companyChannelHandlerList: $companyChannelHandlerList,
        agentList: $agentList
        ) {
        ...AggregateResultFragment
      }
    }
    ${AggregateResultFragment}
  `;
  definition.isAnalytic = true
  return definition
};

// - within SLA

export const queryGetAverageResponseRateWithinSLA = createAnalyticQuery(
  "AverageResponseRateWithinSLA"
);
export const queryGetResponseRateWithinSLA = createAnalyticQuery("ResponseRateWithinSLA");
export const queryGetResponseRateWithinSLAGrowth = createAnalyticQuery(
  "ResponseRateWithinSLAGrowth"
);
export const queryGetResponseRateWithinSLALeaderboard = createAnalyticQuery(
  "ResponseRateWithinSLALeaderboard"
);
// - within SLA end
// - responded count

export const queryGetRespondedCount = createAnalyticQuery("RespondedCount");
export const queryGetTotalRespondedCount = createAnalyticQuery("TotalRespondedCount");
export const queryGetRespondedCountLeaderboard = createAnalyticQuery("RespondedCountLeaderboard");

// - responded count end
export const queryGetAverageResponseRate = createAnalyticQuery(
  "AverageResponseRate"
);
export const queryGetResponseRate = createAnalyticQuery("ResponseRate");
export const queryGetResponseRateGrowth = createAnalyticQuery(
  "RespnseRateGrowth"
);
export const queryGetResponseRateLeaderboard = createAnalyticQuery(
  "ResponseRateLeaderboard"
);
export const queryGetResponseTime = createAnalyticQuery("ResponseTime");
export const queryGetAverageResponseTime = createAnalyticQuery(
  "AverageResponseTime"
);
export const queryGetResponseTimeGrowth = createAnalyticQuery(
  "ResponseTimeGrowth"
);
export const queryGetResponseTimeLeaderboard = createAnalyticQuery(
  "ResponseTimeLeaderboard"
);
export const queryGetHandlingTime = createAnalyticQuery("HandlingTime");
export const queryGetAverageHandlingTime = createAnalyticQuery(
  "AverageHandlingTime"
);
export const queryGetHandlingTimeGrowth = createAnalyticQuery(
  "HandlingTimeGrowth"
);
export const queryGetHandlingTimeLeaderboard = createAnalyticQuery(
  "HandlingTimeLeaderboard"
);
export const queryGetWordCloud = createAnalyticQuery("WordCloud");
export const queryGetPeakTime = createAnalyticQuery("PeakTime");
export const queryGetSinglePeakTime = createAnalyticQuery("SinglePeakTime");
export const queryGetCaseCount = createAnalyticQuery("CaseCount");
export const queryGetTotalCaseCount = createAnalyticQuery("TotalCaseCount");
export const queryGetCustomerCount = createAnalyticQuery("CustomerCount");
export const queryGetTotalCustomerCount = createAnalyticQuery(
  "TotalCustomerCount"
);
export const queryGetCategoryCount = createAnalyticQuery("CategoryCount");
export const queryGetTotalCategoryCount = createAnalyticQuery(
  "TotalCategoryCount"
);

/* Email Campaign */

export const queryGetEmailCampaignAccount = gql`
  query queryGetEmailCampaignAccount {
    getEmailCampaignAccount {
      id
      originalId
      datacenter
      role
      accountName
      email  
    }
  }
`

export const queryGetEmailCampaign = gql`
  query queryGetEmailCampaign($after: DateTime) {
    getEmailCampaign(after: $after) {
      id
      originalId
      webId
      campaignType
      campaignStatus
      dateCreated
      sendStatus {
        sendCount
        sendTime
      }
      resendable
      recipient {
        name
        recipientCount
      }
      summary {
        openCount
        uniqueOpen
        openRate
        clickCount
        clickRate
        subscriberClickCount
      }
      settings {
        subjectLine
        previewText
        title
        fromName
        replyTo
        toName
      }
    }
  }
`

export const queryGetSingleCampaign = gql`
  query queryGetSingleCampaign($campaignId: Int!) {
    getCampaign(campaignId: {id: $campaignId}) {
      id
      originalId
      webId
      campaignType
      campaignStatus
      dateCreated
      sendStatus {
        sendCount
        sendTime
      }
      resendable
      recipient {
        name
        recipientCount
      }
      summary {
        openCount
        uniqueOpen
        openRate
        clickCount
        clickRate
        subscriberClickCount
      }
      settings {
        subjectLine
        previewText
        title
        fromName
        replyTo
        toName
      }
      emailTemplate {
        htmlBody
        thumbnail
      }
    }
  }
`

export const queryGetEmailCampaignAudience = gql`
  query queryGetEmailAudience($after: DateTime) {
    getAudienceList(after: $after) {
      id
      originalId
      dateCreated
      contactInformation {
        companyName
        address1
        address2
        city
        state
        zip
        country
        phone
      }
      campaignDefaults {
        fromName
        fromEmail
        subject
        language
      }
      summary {
        memberCount
        campaignCount
        openRate
        clickRate
      }
    }
  }
`

export const queryGetEmailCampaignAudienceMember = gql`
  query queryGetEmailCampaignAudienceMember($audienceId: Int!, $after: DateTime) {
    getAudienceMember(audienceId: { id: $audienceId }, after: $after) {
      id
      originalId
      emailAddress
      webId
      status
      rating
      source
      location {
        latitude
        longitude
        gmtOffset
        dstOffset
        countryCode
        timezone
      }
      unsubscribeReason
      averageClickRate
      averageOpenRate
      fields {
        firstName
        lastName
        phone
        birthday
        city
        state
        zip
        country
        address1
        address2
      } 
    }
  }
`

export const queryGetEmailCampaignTemplate = gql`
  query queryGetEmailCampaignTemplate($after: DateTime) {
    getEmailTemplate(after: $after) {
      id
      originalId
      name
      templateType
      dateCreated
      thumbnail
    }
  }
`

/* Bulk Sms */

export const queryGetBulkSmsAccount = gql`
  query queryGetBulkSmsAccount {
    getBulkSmsAccount {
      id
    }
  }
`

export const queryGetBulkSmsCampaignList = gql`
  query queryGetBulkSmsCampaignList($after: DateTime) {
    getBulkSmsCampaignList(after: $after) {
      id
      campaignName
      content
      status
      dateCreated
      tagList {
        id
        name
        displayName
      }
    }
  }
`

export const queryGetBulkSmsContactList = gql`
  query queryGetBulkSmsContactList($after: DateTime) {
    getBulkSmsContactList(after: $after) {
      id
      firstName
      lastName
      phoneNumber
      tags {
        id
        name
        displayName
      }
    }
  }
`

export const queryGetBulkSmsContactTagList = gql`
  query queryGetBulkSmsContactTagList($after: DateTime) {
    getBulkSmsContactTagList(after: $after) {
      id
      name
      displayName
    }
  }
`

export const queryGetBulkSmsCampaign = gql`
  query queryGetBulkSmsCampaign($campaignId: Int!) {
    getBulkSmsCampaign(campaignId: $campaignId) {
      id
      campaignName
      content
      status
      dateCreated
      tagList {
        id
        name
        displayName
      }
    }
  }
`

/* Graphql Mutations */

export const mutationAddTicketToCase = gql`
  mutation addTicketToCase($case: CaseInput!, $email: String, $phoneNumber: String, $address: String, $title: String!, $content: String!, $brand: Int!, $product: Int!) {
    createTicket(case: $case, email: $email, phoneNumber: $phoneNumber, address: $address, title: $title, content: $content, brand: $brand, product: $product) {
      ...TicketFragment
    }
  }
  ${TicketFragment}
`

export const mutationAddTemplateLivechat = gql`
  mutation addTemplateLivechat($content: String!, $isActive: Boolean!) {
    addTemplateLivechat(content: $content, isActive: $isActive) {
      ...TemplateLivechatTypeFragment
    }
  }
  ${TemplateLivechatTypeFragment}
`;

export const mutationCekOngkir = gql`
  mutation cekOngkir($originId: Int!, $destinationId: Int!, $weight: Int!) {
    cekOngkir(
      originId: $originId
      destinationId: $destinationId
      weight: $weight
    ) {
      courier
      name
      logo
      service
      description
      cost
      etd
      note
      weight
      origin
      destination
    }
  }
`;

export const mutationUpdateOngkirCompany = gql`
  mutation updateOngkirCompany($template: String!, $toggle: Boolean!) {
    updateOngkirCompany(template: $template, isActive: $toggle) {
      id
      template
      companyId
      isActive
    }
  }
`;

export const mutationAddLiveChatDomain = gql`
  mutation addLiveChatDomain($domain: String!, $displayPicture: String!) {
    addLiveChatDomain(domain: $domain, displayPicture: $displayPicture) {
      ...CompanyChannelHandlerFragment
    }
  }
  ${CompanyChannelHandlerFragment}
`;

export const mutationVerifyDomain = gql`
  mutation verifyDomain($companyChannelId: Int!) {
    verifyDomain(companyChannelId: $companyChannelId) {
      ...CompanyChannelHandlerFragment
    }
  }
  ${CompanyChannelHandlerFragment}
`;

export const mutationChatWithWhatsapp = gql`
  mutation chatWithWhatsapp($case: CaseInput!, $content: String!) {
    chatWithWhatsapp(case: $case, content: $content) {
      ...CaseFragment
    }
  }
  ${CaseFragment}
`;

export const mutationWhatsappRegistration = gql`
  mutation whatsappRegistration(
    $phoneNumber: String!
    $mcc: Int!
    $mnc: Int!
    $cc: Int!
  ) {
    whatsappRegistration(
      phoneNumber: $phoneNumber
      mcc: $mcc
      mnc: $mnc
      cc: $cc
    ) {
      ...GraphBaseResultTypeFragment
    }
  }
  ${GraphBaseResultTypeFragment}
`;

export const mutationWhatsappCodeConfirmation = gql`
  mutation whatsappCodeConfirmation(
    $phoneNumber: String!
    $code: String!
    $channelName: String!
    $cc: Int!
  ) {
    whatsappCodeConfirmation(
      phoneNumber: $phoneNumber
      code: $code
      channelName: $channelName
      cc: $cc
    ) {
      ...GraphBaseResultTypeFragment
    }
  }
  ${GraphBaseResultTypeFragment}
`;

export const mutationAddSchedulerPost = gql`
  mutation addSchedulerPost(
    $postInput: [SchedulerPostInput!]!
    $content: String!
    $dateForPost: DateTime!
    $image: String
  ) {
    addSchedulerPost(
      PostInput: $postInput
      content: $content
      dateForPost: $dateForPost
      image: $image
    ) {
      id
    }
  }
`;

export const mutationAssignCaseToAgent = gql`
  mutation assignCaseToAgent($case: CaseInput!, $agent: AgentInput) {
    assignCaseToAgent(case: $case, agent: $agent) {
      ...CaseFragment
    }
  }
  ${CaseFragment}
`;

export const mutationAssignCaseToOther = gql`
  mutation assignCaseToOther($case: CaseInput!, $agent: AgentInput) {
    assignCaseToOther(case: $case, agent: $agent) {
      ...CaseFragment
    }
  }
  ${CaseFragment}
`;

export const mutationCloseCase = gql`
  mutation closeCase($case: CaseInput!, $isCsatSent: Boolean!) {
    closeCase(case: $case, isCsatSent: $isCsatSent) {
      id
    }
  }
`;

export const mutationCloseCaseWithNote = gql`
  mutation closeCaseWithNote($case: CaseInput!, $isCsatSent: Boolean!, $content: String!) {
    closeCaseWithNote(case: $case, isCsatSent: $isCsatSent, content: $content) {
      ...CaseFragment
    }
  }
  ${CaseFragment}
`;

export const mutationUnassignAgentFromCase = gql`
  mutation unassignAgentFromCase($case: CaseInput!) {
    unassignAgentFromCase(case: $case) {
      ...CaseFragment
    }
  }
  ${CaseFragment}
`;

export const mutationAddNoteToCaseInfo = gql`
  mutation addNoteToCaseInfo($case: CaseInput!, $content: String!) {
    addNoteToCaseInfo(case: $case, content: $content) {
      ...NoteTypeFragment
    }
  }
  ${NoteTypeFragment}
`;

export const mutationAddNoteToCaseClosed = gql`
  mutation addNoteToCaseClosed($case: CaseInput!, $content: String!) {
    addNoteToCaseClosed(case: $case, content: $content) {
      ...NoteTypeFragment
    }
  }
  ${NoteTypeFragment}
`;

export const mutationAddNoteToCustomerInfo = gql`
  mutation addNoteToCustomerInfo(
    $customerInput: CustomerInput!
    $content: String!
  ) {
    addNoteToCustomerInfo(customerInput: $customerInput, content: $content) {
      ...NoteTypeFragment
    }
  }
  ${NoteTypeFragment}
`;

export const mutationAddNoteToPendingStream = gql`
  mutation addNoteToPendingStream(
    $pendingStream: PendingStreamInput!
    $content: String!
  ) {
    addNoteToPendingStream(pendingStream: $pendingStream, content: $content) {
      ...NoteTypeFragment
    }
  }
  ${NoteTypeFragment}
`;

export const mutationReplyCase = gql`
  mutation replyToCase(
    $case: CaseInput!
    $stream: StreamInput!
    $content: String!
    $channelSpecificType: ChannelSpecificTypeInput!
    $image: String
    $replyMode: ReplyModeType
    $categoryLabelInput: [SingleLabelInput!]
  ) {
    replyToCase(
      case: $case
      stream: $stream
      content: $content
      channelSpecificType: $channelSpecificType
      image: $image
      replyMode: $replyMode
      categoryLabelInput: $categoryLabelInput
    ) {
      ...StreamFragment
    }
  }
  ${StreamFragment}
`;

export const mutationDeleteChannel = gql`
  mutation deleteCompanyChannelHandler(
    $companyChannelHandlerArg: CompanyChannelHandlerInput!
  ) {
    deleteCompanyChannelHandler(
      companyChannelHandlerArg: $companyChannelHandlerArg
    ) {
      ...CompanyChannelHandlerFragment
    }
  }
  ${CompanyChannelHandlerFragment}
`;

export const mutationAddAgent = gql`
  mutation addAgent($username: String!, $name: String!, $email: String!) {
    addAgent(username: $username, name: $name, email: $email) {
      ...AgentFragment
    }
  }
  ${AgentFragment}
`;

export const mutationDeleteAgent = gql`
  mutation deleteAgent($agent: AgentInput) {
    deleteAgent(agent: $agent) {
      ...AgentFragment
    }
  }
  ${AgentFragment}
`;

export const mutationAddCustomerFieldType = gql`
  mutation addCustomerFieldType($name: String!, $displayName: String!) {
    addCustomerFieldType(name: $name, displayName: $displayName) {
      ...CustomerFieldTypeFragment
    }
  }
  ${CustomerFieldTypeFragment}
`;

export const mutationUpdateCustomerFieldType = gql`
  mutation updateCustomerFieldType(
    $customerFieldTypeInput: CustomerFieldTypeInput!
  ) {
    updateCustomerFieldType(customerFieldTypeInput: $customerFieldTypeInput) {
      ...CustomerFieldTypeFragment
    }
  }
  ${CustomerFieldTypeFragment}
`;

export const mutationDeleteCustomerFieldType = gql`
  mutation deleteCustomerFieldType(
    $customerFieldTypeInput: CustomerFieldTypeInput!
  ) {
    deleteCustomerFieldType(customerFieldTypeInput: $customerFieldTypeInput) {
      ...CustomerFieldTypeFragment
    }
  }
  ${CustomerFieldTypeFragment}
`;

export const mutationAddFieldToCustomer = gql`
  mutation addFieldToCustomer(
    $customerInput: CustomerInput!
    $customerFieldTypeInput: CustomerFieldTypeInput!
    $content: String!
  ) {
    addFieldToCustomer(
      customerInput: $customerInput
      customerFieldTypeInput: $customerFieldTypeInput
      content: $content
    ) {
      ...CustomerFragment
    }
  }
  ${CustomerFragment}
`;

export const mutationUpdateCustomerField = gql`
  mutation updateCustomerField(
    $customerInput: CustomerInput!
    $customerFieldInput: CustomerFieldInput!
    $content: String!
  ) {
    updateCustomerField(
      customerInput: $customerInput
      customerFieldInput: $customerFieldInput
      content: $content
    ) {
      ...CustomerFragment
    }
  }
  ${CustomerFragment}
`;

export const mutationDeleteFieldFromCustomer = gql`
  mutation deleteFieldFromCustomer(
    $customerInput: CustomerInput!
    $customerFieldInput: CustomerFieldInput!
  ) {
    deleteFieldFromCustomer(
      customerInput: $customerInput
      customerFieldInput: $customerFieldInput
    ) {
      ...CustomerFragment
    }
  }
  ${CustomerFragment}
`;

export const mutationAddCompanyConfiguration = gql`
  mutation addCompanyConfiguration(
    $workingStart: Int!
    $workingEnd: Int!
    $responseRateSla: Int!
    $responseTimeSla: Int!
    $handlingTimeSla: Int!
    $nickActive: Boolean!
    $replyLabelActive: Boolean!
    $iconCompanyActive: Boolean!
    $autoAssignActive: Boolean!
    $csatActive: Boolean!
  ) {
    addCompanyConfiguration(
      workingStart: $workingStart
      workingEnd: $workingEnd
      responseRateSla: $responseRateSla
      responseTimeSla: $responseTimeSla
      handlingTimeSla: $handlingTimeSla
      nickActive: $nickActive
      replyLabelActive: $replyLabelActive
      iconCompanyActive: $iconCompanyActive
      autoAssignActive: $autoAssignActive
      csatActive: $csatActive
    ) {
      ...CompanyConfigurationFragment
    }
  }
  ${CompanyConfigurationFragment}
`;

export const mutationUpdateCompany = gql`
  mutation updateCompany(
    $companyDisplayName: String
    $companyDescription: String
  ) {
    updateCompany(
      companyDisplayName: $companyDisplayName
      companyDescription: $companyDescription
    ) {
      ...CompanyFragment
    }
  }
  ${CompanyFragment}
`;

export const mutationLogout = gql`
  mutation logOut {
    logOut {
      company {
        id
      }
    }
  }
`;

export const mutationLogin = gql`
  mutation logIn($company: String!, $username: String!, $password: String!) {
    logIn(company: $company, username: $username, password: $password) {
      company {
        ...CompanyFragment
      }
    }
  }
  ${CompanyFragment}
`;

export const mutationEscalateCase = gql`
  mutation escalateCase(
    $case: CaseInput!
    $stream: StreamInput!
    $content: String!
    $agent: AgentInput
    $image: String
  ) {
    escalateCase(
      case: $case
      stream: $stream
      content: $content
      agent: $agent
      image: $image
    ) {
      ...PendingStreamFragment
    }
  }
  ${PendingStreamFragment}
`;

export const mutationApproveEscalatedCase = gql`
  mutation approveEscalatedCase($pendingStream: PendingStreamInput!) {
    approveEscalatedCase(pendingStream: $pendingStream) {
      ...StreamFragment
    }
  }
  ${StreamFragment}
`;

export const mutationRejectEscalatedCase = gql`
  mutation rejectEscalatedCase($pendingStream: PendingStreamInput!) {
    rejectEscalatedCase(pendingStream: $pendingStream) {
      ...PendingStreamFragment
    }
  }
  ${PendingStreamFragment}
`;

export const mutationUpdatePendingStream = gql`
  mutation updatePendingStream(
    $pendingStream: PendingStreamInput!
    $contentOpt: String
  ) {
    updatePendingStream(
      pendingStream: $pendingStream
      contentOpt: $contentOpt
    ) {
      ...PendingStreamFragment
    }
  }
  ${PendingStreamFragment}
`;

export const mutationDeletePendingStream = gql`
  mutation deletePendingStream($pendingStream: PendingStreamInput!) {
    deletePendingStream(pendingStream: $pendingStream) {
      ...PendingStreamFragment
    }
  }
  ${PendingStreamFragment}
`;

export const mutationUpdateOnlineStatus = gql`
  mutation updateOnlineStatus(
    $agent: AgentInput
    $onlineStatusTypeInput: OnlineStatusTypeInput!
  ) {
    updateOnlineStatus(
      agent: $agent
      onlineStatusTypeInput: $onlineStatusTypeInput
    ) {
      id
    }
  }
`;

export const mutationAddLabelTemplate = gql`
  mutation addLabelTemplate(
    $name: String!
    $displayName: String!
    $labelType: LabelType
    $order: Int!
    $color: String!
  ) {
    addLabelTemplate(
      name: $name
      displayName: $displayName
      labelType: $labelType
      order: $order
      color: $color
    ) {
      ...LabelTemplateFragment
    }
  }
  ${LabelTemplateFragment}
`;

export const mutationDeleteLabelTemplate = gql`
  mutation deleteLabelTemplate($labels: [SingleLabelInput!]!) {
    deleteLabelTemplate(labels: $labels) {
      ...LabelTemplateFragment
    }
  }
  ${LabelTemplateFragment}
`;

export const mutationUpdateLabelTemplate = gql`
  mutation updateLabelTemplate($labels: [SingleLabelInput!]!) {
    updateLabelTemplate(labels: $labels) {
      id
    }
  }
`;

export const mutationAddLabelToCustomer = gql`
  mutation addLabelToCustomer(
    $customerInput: CustomerInput!
    $labels: [SingleLabelInput!]!
  ) {
    addLabelToCustomer(customerInput: $customerInput, labels: $labels) {
      id
    }
  }
`;

export const mutationDeleteLabelFromCustomer = gql`
  mutation deleteLabelFromCustomer(
    $customerInput: CustomerInput!
    $labels: [SingleLabelInput!]!
  ) {
    deleteLabelFromCustomer(customerInput: $customerInput, labels: $labels) {
      id
    }
  }
`;

export const mutationAddLabelToCase = gql`
  mutation addLabelToCase($case: CaseInput!, $labels: [SingleLabelInput!]!) {
    addLabelToCase(case: $case, labels: $labels) {
      id
    }
  }
`;

export const mutationDeleteLabelFromCase = gql`
  mutation deleteLabelFromCase(
    $case: CaseInput!
    $labels: [SingleLabelInput!]!
  ) {
    deleteLabelFromCase(case: $case, labels: $labels) {
      id
    }
  }
`;

export const mutationAddLabelToAgent = gql`
  mutation addLabelToAgent($agent: AgentInput!, $labels: [SingleLabelInput!]!) {
    addLabelToAgent(agent: $agent, labels: $labels) {
      id
    }
  }
`;

export const mutationDeleteLabelFromAgent = gql`
  mutation deleteLabelFromAgent(
    $agent: AgentInput!
    $labels: [SingleLabelInput!]!
  ) {
    deleteLabelFromAgent(agent: $agent, labels: $labels) {
      id
    }
  }
`;

export const mutationAddLabelToStream = gql`
  mutation addLabelToStream(
    $stream: StreamInput!
    $labels: [SingleLabelInput!]!
  ) {
    addLabelToStream(stream: $stream, labels: $labels) {
      id
    }
  }
`;

export const mutationDeleteLabelFromStream = gql`
  mutation deleteLabelFromStream(
    $stream: StreamInput!
    $labels: [SingleLabelInput!]!
  ) {
    deleteLabelFromStream(stream: $stream, labels: $labels) {
      id
    }
  }
`;

export const mutationUpdateAgentManagement = gql`
  mutation updateAgentProfile(
    $agentDisplayName: String
    $agentDisplayPicture: String
    $agentShortName: String
  ) {
    updateAgentProfile(
      updateAgentDisplayName: $agentDisplayName
      updateAgentDisplayPicture: $agentDisplayPicture
      updateAgentShortName: $agentShortName
    ) {
      ...AgentFragment
    }
  }
  ${AgentFragment}
`;

export const mutationDownloadReport = gql`
  mutation download($email: String!, $startTime: DateTime, $endTime: DateTime) {
    downloadExcel(emailInput: $email, startTime: $startTime, endTime: $endTime)
  }
`;

export const mutationAddAutomationDistribution = gql`
  mutation AddAutomationDistribution(
    $name: String!
    $description: String!
    $distributionConditionList: [DistributionConditionInput!]!
    $performAction: PerformActionInput!
  ) {
    addAutomationDistribution(
      name: $name
      description: $description
      distributionConditionList: $distributionConditionList
      performAction: $performAction
    ) {
      ...DistributionsTypeFragment
    }
  }
  ${DistributionsTypeFragment}
`;

export const mutationUpdateAutomationDistribution = gql`
  mutation UpdateAutomationDistribution(
    $name: String!
    $description: String!
    $distributionConditionList: [DistributionConditionInput!]!
    $performAction: PerformActionInput!
    $id: Int!
    $isShown: Boolean!
  ) {
    updateAutomationDistribution(
      name: $name
      description: $description
      distributionConditionList: $distributionConditionList
      performAction: $performAction
      AutoDistributionId: $id
      isShown: $isShown
    ) {
      ...DistributionsTypeFragment
    }
  }
  ${DistributionsTypeFragment}
`;

export const mutationDeleteAutomationDistribution = gql`
  mutation DeleteAutomationDistribution($id: Int!) {
    deleteAutomationDistribution(AutoDistributionId: $id) {
      ...DistributionsTypeFragment
    }
  }
  ${DistributionsTypeFragment}
`;

export const mutationAddTemplateList = gql`
  mutation AddTemplateList(
    $TemplateListInput: TemplateListInput!
    $TemplateReplyInput: [TemplateReplyInput!]!
  ) {
    addTemplateList(
      TemplateListInput: $TemplateListInput
      TemplateReplyInput: $TemplateReplyInput
    ) {
      ...TemplateListTypeFragment
    }
  }
  ${TemplateListTypeFragment}
`;

export const mutationUpdateTemplateList = gql`
  mutation UpdateTemplateList($id: Int!, $content: String!) {
    updateTemplateList(TemplateListInputId: $id, content: $content) {
      ...TemplateListTypeFragment
    }
  }
  ${TemplateListTypeFragment}
`;

export const mutationUpdateTemplateReply = gql`
  mutation UpdateTemplateReply($id: Int!, $content: String!) {
    updateTemplateReply(TemplateListReplyId: $id, content: $content) {
      ...TemplateReplyFragment
    }
  }
  ${TemplateReplyFragment}
`;

export const mutationDeleteTemplateList = gql`
  mutation DeleteTemplateList($id: Int!) {
    deleteTemplateList(TemplateListInputId: $id) {
      ...TemplateListTypeFragment
    }
  }
  ${TemplateListTypeFragment}
`;

export const mutationDeleteTemplateReply = gql`
  mutation DeleteTemplateReply($id: Int!) {
    deleteTemplateReply(TemplateListReplyId: $id) {
      ...TemplateReplyFragment
    }
  }
  ${TemplateReplyFragment}
`;

export const mutationDeleteSchedulerPost = gql`
  mutation deleteSchedulerPost($SchedulerInputId: Int!) {
    deleteSchedulerPost(SchedulerInputId: $SchedulerInputId) {
      id
    }
  }
`;

// COMPANY COLOR AND NICKNAME
export const mutationAddCompanyNick = gql`
  mutation addCompanyNick($NickNameInput: String!, $Id: Int!) {
    addCompanyNick(
      nickNameInput: $NickNameInput
      companyChannelHandlerInputId: $Id
    ) {
      ...CompanyChannelHandlerFragment
    }
  }
  ${CompanyChannelHandlerFragment}
`;

export const mutationAddCompanyColor = gql`
  mutation addCompanyColor($color: String!, $Id: Int!) {
    addCompanyColor(color: $color, companyChannelHandlerInputId: $Id) {
      ...CompanyChannelHandlerFragment
    }
  }
  ${CompanyChannelHandlerFragment}
`;

export const mutationAddTemplateCsat = gql`
  mutation addTemplateCsat($content: String!) {
    addTemplateCsat(content: $content) {
      ...CsatTemplateFragment
    }
  }
  ${CsatTemplateFragment}
`;

export const mutationUpdateSchedulerPost = gql`
  mutation updateSchedulerPost(
    $PostInput: [SchedulerPostInput!]!
    $content: String!
    $dateForPost: DateTime!
    $image: String
    $SchedulerInputId: Int!
  ) {
    updateSchedulerPost(
      PostInput: $PostInput
      content: $content
      dateForPost: $dateForPost
      image: $image
      SchedulerInputId: $SchedulerInputId
    ) {
      id
    }
  }
`;

export const mutationSetupAccount = gql`
  mutation registrationEmailValidation($code: String!) {
    registrationEmailValidation(code: $code) {
      email
      companyName
      username
    }
  }
`;

export const mutationRegistration = gql`
  mutation Registration(
    $email: String!
    $company: String!
    $username: String!
    $password: String!
    $rePassword: String!
    $refCode: String
  ) {
    Registration(
      email: $email
      company: $company
      username: $username
      password: $password
      rePassword: $rePassword
      refCode: $refCode
    ) {
      agent {
        id
      }
      company {
        id
      }
    }
  }
`;

/** Email Campaign Mutation */
export const mutationCreateCampaign = gql`
  mutation mutationCreateCampaign(
    $accountId: Int!, 
    $audienceId: Int!, 
    $subject: String!,
    $title: String!,
    $preview: String!,
    $fromName: String!
    $replyTo: String!,
    $toName: String!,
    $html: String! 
  ) {
    createCampaignWithTemplate(
      accountId: {id: $accountId},
      audienceId: {id: $audienceId},
      campaignSetting: {
        subject: $subject,
        title: $title,
        preview: $preview,
        fromName: $fromName,
        replyTo: $replyTo,
        toName: $toName
      },
      html: $html
    ) {
      id
      originalId
      webId
      campaignType
      campaignStatus
      dateCreated
      sendStatus {
        sendCount
        sendTime
      }
      resendable
      recipient {
        name
        recipientCount
      }
      settings {
        subjectLine
        previewText
        title
        fromName
        replyTo
        toName
      }
    }
  }
`

export const mutationCreateCampaignAsDraft = gql`
  mutation mutationCreateCampaignAsDraft(
    $accountId: Int!, 
    $audienceId: Int!, 
    $subject: String!,
    $title: String!,
    $preview: String!,
    $fromName: String!
    $replyTo: String!,
    $toName: String!,
    $html: String! 
  ) {
    createCampaignAsDraft(
      accountId: {id: $accountId},
      audienceId: {id: $audienceId},
      campaignSetting: {
        subject: $subject,
        title: $title,
        preview: $preview,
        fromName: $fromName,
        replyTo: $replyTo,
        toName: $toName
      },
      html: $html
    ) {
      id
      originalId
      webId
      campaignType
      campaignStatus
      dateCreated
      sendStatus {
        sendCount
        sendTime
      }
      resendable
      recipient {
        name
        recipientCount
      }
      settings {
        subjectLine
        previewText
        title
        fromName
        replyTo
        toName
      }
    }
  }
`

export const mutationEditDraft = gql`
  mutation mutationUpdateCampaignDraft(
    $accountId: Int!, 
    $campaignId: Int!, 
    $subject: String!,
    $title: String!,
    $preview: String!,
    $fromName: String!
    $replyTo: String!,
    $toName: String!,
    $html: String,
    $triggerSend: Boolean! 
  ) {
    updateCampaignDraft(
      accountId: {id: $accountId},
      campaignId: {id: $campaignId},
      campaignSetting: {
        subject: $subject,
        title: $title,
        preview: $preview,
        fromName: $fromName,
        replyTo: $replyTo,
        toName: $toName
      },
      htmlOpt: $html,
      triggerSend: $triggerSend
    ) {
      id
      originalId
      webId
      campaignType
      campaignStatus
      dateCreated
      sendStatus {
        sendCount
        sendTime
      }
      resendable
      recipient {
        name
        recipientCount
      }
      settings {
        subjectLine
        previewText
        title
        fromName
        replyTo
        toName
      }
    }
  }  
`

export const mutationCreateAudienceImportJob = gql`
  mutation mutationCreateAudienceImportJob
  (
    $accountId: Int!,
    $audienceId: Int!,
    $fileName: String!
  ) {
    uploadContact(
      accountId: {id: $accountId},
      audienceId: {id: $audienceId},
      fileName: $fileName
    ) {
      error
      jobId
    }
  }
`

export const mutationCreateAudienceMember = gql`
  mutation mutationCreateAudienceMember
  (
    $accountId: Int!,
    $audienceId: Int!,
    $firstName: String!,
    $lastName: String!,
    $emailAddress: String!,
    $phoneNumber: String,
    $city: String,
    $state: String,
    $zipCode: String,
    $countryCode: String,
    $phoneNumber: String
  ) {
    uploadContact(
      accountId: {id: $accountId},
      audienceId: {id: $audienceId},
      emailContact: {
        firstName: $firstName,
        lastName: $lastName,
        emailAddress: $emailAddress,
        address: $address,
        city: $city,
        state: $state,
        zipCode: $zipCode,
        countryCode: $countryCode,
        phoneNumber: $phoneNumber
      }
    ) {
      id
      emailAddress
      fields {
        firstName
        lastName
      }
    }
  }
`

/* Bulk Sms Mutation */

export const mutationCreateBulkSmsDraft = gql`
  mutation mutationCreateBulkSmsDraft($accountId: Int!, $content: String!, $campaignName: String!, $tagList: [Int!]) {
    createDraft(accountId: $accountId, content: $content, campaignName: $campaignName, tagList: $tagList) {
      id
      campaignName
      content
      status
      dateCreated
      dateUpdated
      dateSent
      tagList { 
        id
        name
        displayName
      }
    }
  }
`

export const mutationUpdateBulkSmsDraft = gql`
  mutation mutationUpdateBulkSmsDraft($campaignId: Int!, $content: String!, $campaignName: String!, $tagList: [Int!]) {
    updateDraft(campaignId: $campaignId, content: $content, campaignName: $campaignName, tagList: $tagList) {
      id
      campaignName
      content
      status
      dateCreated
      dateUpdated
      dateSent
      tagList { 
        id
        name
        displayName
      }
    }  
  }
`

export const mutationCreateImportContactJob = gql`
  mutation mutationCreateImportContactJob($accountId: Int!, $fileName: String!) {
    createImportJob(accountId: $accountId, fileName: $fileName) {
      id
      fileName
      status
    }
  }
`

export const mutationTriggerSendCampaign = gql`
  mutation mutationTriggerSendCampaign($campaignId: Int!) {
    sendCampaign(campaignId: $campaignId) {
      id
      campaignName
      content
      status
      dateCreated
      dateUpdated
      dateSent
      tagList { 
        id
        name
        displayName
      }
    }    
  }
`

export const mutationCreateTag = gql`
  mutation mutationCreateTag($accountId: Int!, $displayName: String!) {
    createTag(accountId: $accountId, displayName: $displayName) {
      id
      name
      displayName
    }
  }
`

export const mutationEditContact = gql`
  mutation mutationEditContact($contactId: Int!, $firstName: String, $lastName: String, $phoneNumber: String, $tagList: [Int!]) {
    editContact(contactId: $contactId, firstName: $firstName, lastName: $lastName, phoneNumber: $phoneNumber, tagList: $tagList) {
      id
      firstName
      lastName
      phoneNumber
      tags {
        id
        name
        displayName
      }
    }
  } 
`

export const mutationAddContact = gql`
  mutation mutationAddContact($accountId: Int!, $firstName: String!, $lastName: String!, $phoneNumber: String!, $tagList: [Int!]) {
    addContact(accountId: $accountId, firstName: $firstName, lastName: $lastName, phoneNumber: $phoneNumber, tagList: $tagList) {
      id
      firstName
      lastName
      phoneNumber
      tags {
        id
        name
        displayName
      }
    }
  } 
`

export const mutationEditTag = gql`
  mutation mutationEditTag($tagId: Int!, $displayName: String) {
    editTag(tagId: $tagId, displayName: $displayName) {
      id
      name
      displayName
    }
  }
`

export const mutationDeleteCampaign = gql`
  mutation mutationDeleteCampaign($campaignId: Int!) {
    deleteCampaign(campaignId: $campaignId) {
      id
    }
  }
`

export const mutationDeleteContact = gql`
  mutation mutationDeleteContact($contactId: Int!) {
    deleteContact(contactId: $contactId) {
      id
    }
  }
`

export const mutationDeleteTag = gql`
  mutation mutationDeleteTag($tagId: Int!) {
    deleteTag(tagId: $tagId) {
      id
    }
  }
`

/* SUBSCRIPTION */
export const subsOnCaseAggregateCountUpdated = gql`
  subscription onCaseAggregateCountUpdated {
    onCaseAggregateCountUpdated {
      assignedToCount
      lastReplyCount
      allCount
      pendingApprovalCount
    }
  }
`;

export const subsOnStreamAdded = gql`
  subscription onStreamAdded {
    onStreamAdded {
      ...StreamFragment
    }
  }
  ${StreamFragment}
`;

export const subsOnLivechatOnlineStatusUpdated = gql`
  subscription onLivechatOnlineStatusUpdated {
    onLivechatOnlineStatusUpdated {
      id
      originalId
      isOnline
      dateUpdated
      dateCreated
    }
  }
`;

export const subsOnStreamUpdated = gql`
  subscription onStreamUpdated {
    onStreamUpdated {
      ...StreamFragment
    }
  }
  ${StreamFragment}
`;

export const subsOnCaseAdded = gql`
  subscription onCaseAdded {
    onCaseAdded {
      ...CaseFragment
    }
  }
  ${CaseFragment}
`;

export const subsOnCaseUpdated = gql`
  subscription onCaseUpdated {
    onCaseUpdated {
      ...CaseFragment
    }
  }
  ${CaseFragment}
`;

export const subsOnPendingStreamAdded = gql`
  subscription onPendingStreamAdded {
    onPendingStreamAdded {
      ...PendingStreamFragment
    }
  }
  ${PendingStreamFragment}
`;

export const subsOnPendingStreamUpdated = gql`
  subscription onPendingStreamUpdated {
    onPendingStreamUpdated {
      ...PendingStreamFragment
    }
  }
  ${PendingStreamFragment}
`;

export const subsOnPendingStreamDeleted = gql`
  subscription onPendingStreamDeleted {
    onPendingStreamDeleted {
      ...PendingStreamFragment
    }
  }
  ${PendingStreamFragment}
`;

export const subsOnSchedulerPostAdded = gql`
  subscription onSchedulerPostAdded {
    onSchedulerPostAdded {
      ...SchedulerPostFragment
    }
  }
  ${SchedulerPostFragment}
`;

export const subsOnSchedulerPostUpdated = gql`
  subscription onSchedulerPostUpdated {
    onSchedulerPostUpdated {
      ...SchedulerPostFragment
    }
  }
  ${SchedulerPostFragment}
`;

export const subsOnNotificationAdded = gql`
  subscription onNotificationAdded {
    onNotificationAdded {
      ...NotificationGroupTypeFragment
    }
  }
  ${NotificationGroupTypeFragment}
`;

/* Direct cache access */
export function getAgent(id) {
  return client.readFragment({
    id: "Agent:" + id,
    fragment: AgentFragment
  });
}

export function getMe() {
  const me = client.readQuery({
    query: queryGetMe
  });
  if (me) return me.me;
}

/* Direct mutation */
export async function RejectEscalatedCase(id, content) {
  await client.mutate({
    mutation: mutationAddNoteToPendingStream,
    variables: {
      pendingStream: { id },
      content: content
    }
  });

  await client.mutate({
    mutation: mutationRejectEscalatedCase,
    variables: {
      pendingStream: { id }
    }
  });
}

export async function GetNoNeedLabelId() {
  // Get no need label id
  try {
    const result = await client.query({
      query: queryGetLabelsByType,
      variables: {
        type: "STREAM"
      }
    });
    const label = result.data.getLabelTemplates.find(
      label => label.name === "No Need"
    );
    return label ? label.id : -1;
  } catch (e) {
    return -1;
  }
}



// WEBPACK FOOTER //
// ./src/Apollo.js